import { ActiveProjectProvider } from "@/features/project/ActiveProject";
import { Quickview } from "@/features/projects/modals/Quickview";
import styled from "@emotion/styled";
import { type CoworkerBriefAction } from "@repo/api";
import {
  LightMode,
  ListView,
  ListViewItem,
  Text,
  Variables,
} from "@repo/skapa";
import {
  DEFAULT_LOCALE,
  getLanguageAndLocaleFromUrl,
  t,
} from "@repo/translation";
import { useState } from "react";

interface NextStepsProps {
  steps: CoworkerBriefAction[];
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  > * {
    margin-bottom: ${Variables.space50};
  }
`;

const NextStepsList = styled(ListView)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${Variables.space100};

  > * {
    flex: 0 1 calc(20% - ${Variables.space100});
    width: auto;
  }
`;

const NestStepListItem = styled(ListViewItem)`
  border: 1px solid ${LightMode.colourNeutral3};
`;

export const NextSteps = ({ steps }: NextStepsProps) => {
  const [activeOpportunityId, setActiveOpportunityId] = useState<
    number | undefined
  >(undefined);

  const locale = getLanguageAndLocaleFromUrl();
  const { language } = locale ?? DEFAULT_LOCALE;

  return (
    <>
      <Container>
        <Header>
          <Text textStyle="Label.M">{t("label.ai_next_step_title")}</Text>
          <Text>{t("label.ai_next_step_subtitle")}</Text>
        </Header>

        <NextStepsList id="next-steps-list">
          {steps.map((step) => (
            <NestStepListItem
              key={step.projectId}
              title={`${t("label.project_number", {
                number: step.projectId,
              })}`}
              addOn={<Text textStyle="Caption.S">{step.action[language]}</Text>}
              control="navigational"
              onClick={() => setActiveOpportunityId(Number(step.projectId))}
              size="small"
              inset
            />
          ))}
        </NextStepsList>
      </Container>
      <ActiveProjectProvider opportunityId={activeOpportunityId}>
        <Quickview
          opportunityId={activeOpportunityId}
          closeQuickView={() => setActiveOpportunityId(undefined)}
        />
      </ActiveProjectProvider>
    </>
  );
};
